import React from 'react';
import type {
  AdminLicenseDto,
  AdminMutationOutcome,
  AdminUserCreateRequest,
  AdminUserDto,
  AdminUserPatchRequest,
  AdminUserStatus,
} from '../types';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  changeUserDraftCompany,
  createNewUserDraft,
  createUserCreateRequest,
  createUserDraft,
  createUserPatchRequest,
  hasDeliverableUserEmail,
  isUserDraftDirty,
  validateUserDraft,
  type UserDraft,
  type UserDraftErrors,
} from '../lib/adminUsers';
import { useAdminToast } from '../ui/components';

type UserEditorMode = 'invite' | 'edit';

export interface UserDraftSession {
  draft: UserDraft;
  dirty: boolean;
  validation: UserDraftErrors;
  fieldErrors: UserDraftErrors;
  saveError: string | null;
  assignmentNotice: string | null;
  createPatch: () => AdminUserPatchRequest | null;
  setField: <Key extends keyof UserDraft>(key: Key, value: UserDraft[Key]) => void;
  changeCompany: (companyId: number) => void;
  toggleLicense: (licenseId: number) => void;
  reconcile: (canonical: AdminUserDto) => void;
  setApiError: (error: unknown) => ReturnType<typeof getAdminApiError>;
  clearErrors: () => void;
}

const FieldByErrorCode: Record<string, keyof UserDraft> = {
  user_email_conflict: 'email',
  invalid_user_email: 'email',
  invalid_user_username: 'username',
  invalid_user_company: 'companyId',
  invalid_user_role: 'role',
  super_promotion_forbidden: 'role',
  invalid_user_status: 'status',
  invalid_user_licenses: 'licenseIds',
};

const withoutDraftError = (
  errors: UserDraftErrors,
  field: keyof UserDraft
): UserDraftErrors => {
  if (!(field in errors)) {
    return errors;
  }
  const next = { ...errors };
  delete next[field];
  return next;
};

const toggleDraftLicense = (draft: UserDraft, licenseId: number): UserDraft => ({
  ...draft,
  licenseIds: draft.licenseIds.includes(licenseId)
    ? draft.licenseIds.filter((id) => id !== licenseId)
    : [...draft.licenseIds, licenseId],
});

export const useUserDraftSession = (
  open: boolean,
  mode: UserEditorMode,
  user: AdminUserDto | null,
  licenses: readonly AdminLicenseDto[],
  firstCompanyId: number
): UserDraftSession => {
  const userId = user?.id ?? null;
  const initialUserId = React.useRef<number | null>(null);
  const initialUser = React.useRef<AdminUserDto | null>(null);
  if (userId !== initialUserId.current) {
    initialUserId.current = userId;
    initialUser.current = user;
  }
  const [draft, setDraft] = React.useState(() => createNewUserDraft(firstCompanyId));
  const [original, setOriginal] = React.useState(draft);
  const [fieldErrors, setFieldErrors] = React.useState<UserDraftErrors>({});
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [assignmentNotice, setAssignmentNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const canonical = initialUser.current;
    const initial = mode === 'edit' && canonical
      ? createUserDraft(canonical)
      : createNewUserDraft(firstCompanyId);
    setDraft(initial);
    setOriginal(initial);
    setFieldErrors({});
    setSaveError(null);
    setAssignmentNotice(null);
  }, [firstCompanyId, mode, open, userId]);

  const clearErrors = () => {
    setFieldErrors({});
    setSaveError(null);
  };
  const clearFieldError = (key: keyof UserDraft) =>
    setFieldErrors((current) => withoutDraftError(current, key));
  const setField = <Key extends keyof UserDraft>(key: Key, value: UserDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    clearFieldError(key);
    setSaveError(null);
  };
  const changeCompany = (companyId: number) => {
    const result = changeUserDraftCompany(draft, companyId, licenses);
    setDraft(result.draft);
    if (result.removedLicenseIds.length > 0) {
      setAssignmentNotice(
        `Removed ${result.removedLicenseIds.length} license assignments because they do not belong to the selected company.`
      );
    }
    clearFieldError('companyId');
    clearFieldError('licenseIds');
    setSaveError(null);
  };
  const reconcile = (canonical: AdminUserDto) => {
    const next = createUserDraft(canonical);
    setDraft(next);
    setOriginal(next);
    setAssignmentNotice(null);
    clearErrors();
  };
  const setApiError = (error: unknown) => {
    const failure = getAdminApiError(error, 'user_save_failed');
    const field = FieldByErrorCode[failure.code];
    if (field) {
      setFieldErrors((current) => ({ ...current, [field]: failure.message }));
    } else {
      setSaveError(failure.message);
    }
    return failure;
  };

  return {
    draft,
    dirty: isUserDraftDirty(draft, original),
    validation: validateUserDraft(draft, mode, licenses, original),
    fieldErrors,
    saveError,
    assignmentNotice,
    createPatch: () => createUserPatchRequest(draft, original),
    setField,
    changeCompany,
    toggleLicense: (licenseId) => {
      setDraft((current) => toggleDraftLicense(current, licenseId));
      clearFieldError('licenseIds');
      setSaveError(null);
    },
    reconcile,
    setApiError,
    clearErrors,
  };
};

export const useUserDirtyClose = (
  dirty: boolean,
  blocked: boolean,
  onClose: () => void
) => {
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const resolver = React.useRef<((discard: boolean) => void) | null>(null);
  const resolveDiscard = (discard: boolean) => {
    setDiscardOpen(false);
    resolver.current?.(discard);
    resolver.current = null;
  };
  const confirmDirtyClose = () => {
    if (blocked || discardOpen) {
      return Promise.resolve(false);
    }
    setDiscardOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  };
  const requestCancel = async () => {
    if (dirty && !await confirmDirtyClose()) {
      return;
    }
    onClose();
  };
  return { confirmDirtyClose, discardOpen, requestCancel, resolveDiscard };
};

interface UserMutationOptions {
  mode: UserEditorMode;
  user: AdminUserDto | null;
  loading: boolean;
  session: UserDraftSession;
  onClose: () => void;
  onCreate: (request: AdminUserCreateRequest) => Promise<AdminUserDto>;
  onSave: (id: number, patch: AdminUserPatchRequest) => Promise<AdminUserDto>;
  onDelete: (id: number) => Promise<AdminMutationOutcome>;
  onResetPassword: (id: number) => Promise<unknown>;
  onChangeStatus: (id: number, status: AdminUserStatus) => Promise<AdminUserDto>;
}

const useMutationLock = () => {
  const [submitting, setSubmitting] = React.useState(false);
  const mutationLock = React.useRef(false);
  const beginMutation = () => {
    if (mutationLock.current) {
      return false;
    }
    mutationLock.current = true;
    setSubmitting(true);
    return true;
  };
  const finishMutation = () => {
    mutationLock.current = false;
    setSubmitting(false);
  };
  return { beginMutation, finishMutation, submitting };
};

export const useUserMutations = (options: UserMutationOptions) => {
  const { showToast } = useAdminToast();
  const { beginMutation, finishMutation, submitting } = useMutationLock();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const hasErrors = Object.keys(options.session.validation).length > 0;
  const canSubmit = !hasErrors && !submitting && !options.loading
    && (options.mode === 'invite' || options.session.dirty);

  const submit = async () => {
    if (!canSubmit || !beginMutation()) {
      return;
    }
    options.session.clearErrors();
    try {
      if (options.mode === 'invite') {
        await options.onCreate(createUserCreateRequest(options.session.draft));
        showToast('Invitation queued for email delivery.', { tone: 'info' });
        options.onClose();
      } else if (options.user) {
        const patch = options.session.createPatch();
        if (!patch) {
          return;
        }
        const canonical = await options.onSave(options.user.id, patch);
        options.session.reconcile(canonical);
        showToast('User saved.');
        options.onClose();
      }
    } catch (error) {
      const failure = options.session.setApiError(error);
      showToast(`User save failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      finishMutation();
    }
  };
  const resetPassword = async () => {
    if (!options.user
      || !hasDeliverableUserEmail(options.user.email)
      || !beginMutation()) {
      return;
    }
    try {
      await options.onResetPassword(options.user.id);
      showToast('Password reset request accepted.', { tone: 'info' });
    } catch (error) {
      const failure = getAdminApiError(error, 'user_reset_failed');
      showToast(`Password reset failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      finishMutation();
    }
  };
  const setStatus = async (status: AdminUserStatus) => {
    if (!options.user || !beginMutation()) {
      return;
    }
    try {
      const canonical = await options.onChangeStatus(options.user.id, status);
      options.session.reconcile(canonical);
      showToast(status === 'blocked' ? 'User blocked.' : 'User unblocked.');
    } catch (error) {
      const failure = getAdminApiError(error, 'user_status_change_failed');
      showToast(`User status change failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      finishMutation();
    }
  };
  const confirmDelete = async () => {
    if (!options.user || !beginMutation()) {
      return;
    }
    try {
      const result = await options.onDelete(options.user.id);
      setDeleteOpen(false);
      showToast(result.refreshError
        ? `User deleted; canonical refresh failed (${result.refreshError.code}).`
        : 'User deleted.', result.refreshError ? { tone: 'warning' } : undefined);
      options.onClose();
    } catch (error) {
      const failure = getAdminApiError(error, 'user_delete_failed');
      showToast(`User delete failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      finishMutation();
    }
  };
  return {
    canSubmit, confirmDelete, deleteOpen, resetPassword, setDeleteOpen,
    setStatus, submit, submitting,
  };
};
